/**
 * Accounts (paywall plan P1; sign-in redesign 2026-11) — the Lab43 identity: one Supabase project, shared with
 * auth.lab43.dev, GameSweep and the Minecraft mod.
 *
 * Accounts are only needed to plan *together*. Solo planning never touches this file's network code: the Supabase
 * client is loaded lazily (dynamic import) and only when someone opens the sign-in dialog, comes back from a link that
 * carries a sign-in code (?code=…), or already has a stored session. A solo user makes zero requests to Supabase
 * (checked by tools/e2e/phase-p1.mjs).
 *
 * Ways in (all work the same in the browser and in the desktop app, no redirects needed):
 *   - email + password                       signInWithPassword
 *   - an emailed sign-in code                signInWithOtp → verifyOtp(email); also creates the free account if new
 *   - a new account with a password          signUp → code → verifyOtp(signup) → the password is set again (see
 *                                            finishSignUp: the identity project drops a password set before the email
 *                                            was confirmed, lab43-auth migration …_unconfirmed_password)
 *   - forgot the password                    resetPasswordForEmail → code → verifyOtp(recovery) → setPassword
 * Discord / Google / Microsoft are no longer offered (removed from the UI by the owner, 2026-11).
 *
 * The password rules are the identity project's (lab43-auth apps/accounts/lib/password.ts: 10 characters, a letter and a
 * digit, at most 72 bytes); Supabase has the last word and its refusals are put into plain words here.
 */
import { create } from "zustand";
import type { AuthError, Session, SupabaseClient, User } from "@supabase/supabase-js";
import { isDesktop } from "./desktop.ts";
import { currentVia } from "./via.ts";

/** Supabase project of MC Planner (public values; the publishable key is meant to be shipped in apps). */
export const SUPABASE_URL = ((import.meta.env?.VITE_SUPABASE_URL as string | undefined) || "https://ggqvbjratlsivvztsind.supabase.co").replace(/\/+$/, "");
export const SUPABASE_KEY = (import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ?? "sb_publishable_f7_fuPjapsQbKOZxwW5qWw_6sA8rrdt";
/** false in builds made with VITE_SUPABASE_PUBLISHABLE_KEY="" (e.g. self-hosted without accounts): sign-in is hidden */
export const accountsEnabled = () => !!SUPABASE_KEY;

const STORAGE_KEY = "mcp-auth";

/**
 * Digits in an emailed code: the identity project's *Email OTP length* (8; lab43-auth OTP_LENGTH — change both
 * together, or build with VITE_AUTH_CODE_LENGTH). The code boxes start with this many and grow when a longer code is
 * pasted or typed (up to CODE_MAX).
 */
export const CODE_LENGTH = Math.min(10, Math.max(6, Number(import.meta.env?.VITE_AUTH_CODE_LENGTH) || 8));
export const CODE_MAX = 10;
/** the identity project's *Minimum password length* (lab43-auth PASSWORD_MIN); change both together */
export const PASSWORD_MIN = 10;
const PASSWORD_MAX_BYTES = 72;

export interface Profile { displayName: string; color: string }
export interface AccountState {
  status: "signed-out" | "checking" | "signed-in";
  user: { id: string; email: string | null } | null;
  profile: Profile | null;
  /** this account has a password we know of (signed in with it, or set it here): "Change password" vs "Set a password" */
  hasPassword: boolean;
  /**
   * the sign-in dialog; `reason` says why it opened (e.g. "Planning together needs a free account…"); `step` opens it
   * at a given step ("new-password": back from a password-reset link)
   */
  dialog: { open: boolean; reason?: string; step?: "new-password" };
  /** the account menu (the account button's dropdown), so a banner's "Manage" can open it */
  menu: boolean;
  set: (p: Partial<AccountState>) => void;
}

export const useAccount = create<AccountState>((set) => ({
  status: "signed-out",
  user: null,
  profile: null,
  hasPassword: false,
  dialog: { open: false },
  menu: false,
  set: (p) => set(p),
}));

export function openSignIn(reason?: string) {
  useAccount.getState().set({ dialog: { open: true, reason }, menu: false });
}
export function closeSignIn() {
  useAccount.getState().set({ dialog: { open: false } });
}
export const openAccountMenu = (open = true) => useAccount.getState().set({ menu: open });

// ---------------- errors in plain words ----------------

export type AuthField = "email" | "password" | "again" | "current" | "code" | "name" | "form";

/** A sign-in problem, with the field it belongs under (`form`: under the main button). */
export class AuthProblem extends Error {
  field: AuthField;
  constructor(message: string, field: AuthField = "form") {
    super(message);
    this.field = field;
  }
}

const offline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const OFFLINE = "You're offline. Signing in needs the internet.";
const CODE_FAILED = "That code didn't work. Check the newest email, or send a new code.";

function weakPassword(reasons: readonly string[] = [], message = "") {
  const parts: string[] = [];
  if (reasons.includes("length")) {
    const min = Number(/at least (\d+) characters/i.exec(message)?.[1]) || PASSWORD_MIN;
    parts.push(`Use at least ${Math.max(min, PASSWORD_MIN)} characters.`);
  }
  if (reasons.includes("characters")) parts.push("Include a letter (a–z) and a digit (0–9).");
  if (reasons.includes("pwned")) parts.push("This password is known from data leaks. Choose a different one.");
  return parts.join(" ") || "That password is too easy to guess. Choose a longer one.";
}

/** Supabase Auth's answer → a sentence and its field. `ctx` picks the field for the ambiguous cases. */
export function authProblem(e: unknown, ctx: "signin" | "send" | "code" | "password" = "signin"): AuthProblem {
  if (e instanceof AuthProblem) return e;
  const err = e as Partial<AuthError> & { reasons?: string[]; weak_password?: { reasons?: string[] } };
  const msg = err?.message ?? String(e);
  const code = err?.code as string | undefined;
  if (offline()) return new AuthProblem(OFFLINE);
  if (code === "over_email_send_rate_limit" || /security purposes/i.test(msg)) return new AuthProblem("A code went out a moment ago. Wait a minute, then ask again.");
  if (err?.status === 429 || code === "over_request_rate_limit" || /rate limit|too many/i.test(msg)) return new AuthProblem("Too many tries. Wait a minute.");
  if (err?.name === "AuthRetryableFetchError" || /failed to fetch|networkerror|load failed|fetch failed/i.test(msg)) {
    return new AuthProblem((err?.status ?? 0) >= 500 ? "The sign-in server isn't answering. Try again in a moment." : "Can't reach the sign-in server. Check your internet connection.");
  }
  if ((err?.status ?? 0) >= 500) return new AuthProblem("The sign-in server isn't answering. Try again in a moment.");
  if (code === "otp_expired" || code === "otp_disabled" || /expired|invalid.*(otp|token)|token.*(invalid|expired)/i.test(msg)) return new AuthProblem(CODE_FAILED, "code");
  if (code === "invalid_credentials" || /invalid login credentials/i.test(msg)) return new AuthProblem("Wrong email or password.", "password");
  if (code === "email_not_confirmed") return new AuthProblem("This email isn't confirmed yet. Use “Email me a sign-in code” instead.", "email");
  if (code === "weak_password" || err?.name === "AuthWeakPasswordError") return new AuthProblem(weakPassword(err.reasons ?? err.weak_password?.reasons, msg), "password");
  if (code === "same_password") return new AuthProblem("That's already your password. Choose a different one.", "password");
  if (code === "signup_disabled" || /signups? not allowed/i.test(msg)) return new AuthProblem("New accounts can't be made right now.", "email");
  if (code === "email_address_invalid" || code === "email_address_not_authorized" || /email.*(invalid|format)|unable to validate email/i.test(msg)) return new AuthProblem("Enter a valid email address.", "email");
  if (code === "validation_failed" && /longer than|72/i.test(msg)) return new AuthProblem("That password is too long: 72 characters at most.", "password");
  if (err?.name === "AuthSessionMissingError" || code === "session_not_found" || code === "bad_jwt") return new AuthProblem("Your sign-in has run out. Sign in again.");
  return new AuthProblem(msg || (ctx === "password" ? "The password couldn't be saved. Try again in a moment." : "That didn't work. Try again in a moment."));
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function checkEmail(email: string) {
  if (!email.trim()) return new AuthProblem("Enter your email address.", "email");
  if (!EMAIL.test(email.trim())) return new AuthProblem("That doesn't look like an email address.", "email");
  return null;
}

/** The rules a new password must meet, for the live list under the field. */
export function passwordRules(password: string, again: string) {
  const bytes = new TextEncoder().encode(password).length;
  return [
    { id: "length", ok: [...password].length >= PASSWORD_MIN && bytes <= PASSWORD_MAX_BYTES, label: `At least ${PASSWORD_MIN} characters` },
    { id: "mix", ok: /[A-Za-z]/.test(password) && /[0-9]/.test(password), label: "A letter and a digit" },
    { id: "same", ok: !!again && again === password, label: "Both the same" },
  ];
}
/** null when the new password (typed twice) is fine; else what to fix, under which field */
export function checkNewPassword(password: string, again: string): AuthProblem | null {
  if (!password) return new AuthProblem("Choose a password.", "password");
  if ([...password].length < PASSWORD_MIN) return new AuthProblem(`Use at least ${PASSWORD_MIN} characters.`, "password");
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) return new AuthProblem("That password is too long: 72 characters at most.", "password");
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return new AuthProblem("Include a letter (a–z) and a digit (0–9).", "password");
  if (!again) return new AuthProblem("Type the password again.", "again");
  if (again !== password) return new AuthProblem("The two passwords aren't the same.", "again");
  return null;
}

// ---------------- lazy client ----------------

let clientPromise: Promise<SupabaseClient> | null = null;

export function supabase(): Promise<SupabaseClient> {
  if (!accountsEnabled()) return Promise.reject(new AuthProblem("Accounts aren't available in this version of MC Planner."));
  clientPromise ??= import("@supabase/supabase-js").then(({ createClient }) => {
    const c = createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: { storageKey: STORAGE_KEY, flowType: "pkce", persistSession: true, autoRefreshToken: true, detectSessionInUrl: !isDesktop() },
    });
    c.auth.onAuthStateChange((event, session) => {
      void applySession(session);
      // a password-reset link opened in this browser (the email may carry a link instead of a code): choose a new one
      if (event === "PASSWORD_RECOVERY" && !holding) useAccount.getState().set({ dialog: { open: true, step: "new-password" } });
    });
    return c;
  }).catch((e) => {
    clientPromise = null; // e.g. offline when the chunk loads: try again next time
    throw offline() ? new AuthProblem(OFFLINE) : e;
  });
  return clientPromise;
}

function storedSession() {
  try { return !!localStorage.getItem(STORAGE_KEY); } catch { return false; }
}
/** back from a link that carries a sign-in code (?code=…) this browser asked for (a password-reset link) */
function returningWithCode() {
  try { return new URLSearchParams(location.search).has("code") && !!localStorage.getItem(`${STORAGE_KEY}-code-verifier`); } catch { return false; }
}

/** While a sign-in step runs that has more steps after it (new profile, new password), the dialog stays open. */
let holding = 0;
async function hold<T>(f: () => Promise<T>): Promise<T> {
  holding++;
  try { return await f(); } finally { holding--; }
}

const pwKey = (id: string) => `mcp-has-password:${id}`;
function knownPassword(id: string, token: string) {
  try { if (localStorage.getItem(pwKey(id))) return true; } catch { /* ignore */ }
  try {
    // the access token says how this session started (amr: password, otp, …)
    const claims = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as { amr?: { method?: string }[] };
    return !!claims.amr?.some((a) => a?.method === "password");
  } catch { return false; }
}
function rememberPassword(id: string | undefined) {
  if (!id) return;
  try { localStorage.setItem(pwKey(id), "1"); } catch { /* ignore */ }
  if (useAccount.getState().user?.id === id) useAccount.getState().set({ hasPassword: true });
}

/**
 * A brand-new account: its email was confirmed by this very sign-in (Supabase stamps the confirmation and the sign-in
 * in the same request, both with the server's clock). Only asked right after a code was verified.
 */
function isNewAccount(u: User | null | undefined) {
  if (!u) return false;
  const confirmed = Date.parse(u.email_confirmed_at ?? u.confirmed_at ?? "");
  const last = Date.parse(u.last_sign_in_at ?? "");
  return Number.isFinite(confirmed) && Number.isFinite(last) && Math.abs(last - confirmed) < 10_000;
}

let lastUserId: string | null = null;
async function applySession(session: Session | null) {
  const st = useAccount.getState();
  if (!session?.user) {
    lastUserId = null;
    if (st.status !== "signed-out") st.set({ status: "signed-out", user: null, profile: null, hasPassword: false, menu: false });
    return;
  }
  const user = { id: session.user.id, email: session.user.email ?? null };
  if (lastUserId === user.id && st.status === "signed-in") return;
  lastUserId = user.id;
  const fallback: Profile = { displayName: (user.email ?? "Player").split("@")[0].slice(0, 40), color: "#6fbf4a" };
  st.set({
    status: "signed-in", user, profile: st.user?.id === user.id ? st.profile ?? fallback : fallback, hasPassword: knownPassword(user.id, session.access_token),
    ...(holding ? {} : { dialog: { open: false } }),
  });
  const profile = await loadProfile(user.id).catch(() => null);
  if (profile && useAccount.getState().user?.id === user.id) useAccount.getState().set({ profile });
}

/** Called once on startup. Does nothing (no network, no import) for people who never signed in. */
export async function initAuth() {
  if (!accountsEnabled()) return;
  const withCode = !isDesktop() && returningWithCode();
  if (!withCode && !storedSession()) return;
  useAccount.getState().set({ status: "checking" });
  try {
    const c = await supabase();
    const { data } = await c.auth.getSession(); // also finishes a ?code=… link (detectSessionInUrl)
    await applySession(data.session);
  } catch (e) {
    console.warn("sign-in check failed", e);
    useAccount.getState().set({ status: "signed-out" });
  }
  if (withCode) {
    const u = new URL(location.href);
    for (const k of ["code", "error", "error_code", "error_description", "state"]) u.searchParams.delete(k);
    history.replaceState(null, "", u.pathname + u.search + u.hash);
  }
}

// ---------------- sign-in methods ----------------

/**
 * lab43_app: a new account counts as made in MC Planner in Lab43 HQ's sign-up numbers; lab43_via: the site that sent this
 * visitor, when mc-planner.com's link said (lib/via.ts), counted when the account is confirmed and kept nowhere
 * (lab43-auth docs/API.md → Stats for HQ). Supabase uses both only when the request creates the account.
 */
function newAccountData() {
  const via = currentVia();
  return via ? { lab43_app: "mcplanner", lab43_via: via } : { lab43_app: "mcplanner" };
}

async function client() {
  if (offline()) throw new AuthProblem(OFFLINE);
  return supabase();
}

export async function signInWithPassword(email: string, password: string) {
  const c = await client();
  const { data, error } = await c.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw authProblem(error, "signin");
  rememberPassword(data.user?.id);
  await applySession(data.session);
}

/** Emails a sign-in code (creates the free account if the address is new, unless `existingOnly`). */
export async function sendEmailCode(email: string, { existingOnly = false } = {}) {
  const c = await client();
  const { error } = await c.auth.signInWithOtp({ email: email.trim(), options: existingOnly ? { shouldCreateUser: false } : { shouldCreateUser: true, data: newAccountData() } });
  if (error) throw authProblem(error, "send");
}

/**
 * Signs in with the emailed code. `hold`: keep the sign-in dialog open afterwards (the dialog shows the new-account
 * profile step, or closes itself). `isNew`: the code just confirmed a brand-new account.
 */
export async function verifyEmailCode(email: string, code: string, { hold: keep = false } = {}) {
  const run = async () => {
    const c = await client();
    const { data, error } = await c.auth.verifyOtp({ email: email.trim(), token: code.replace(/\s+/g, ""), type: "email" });
    if (error) throw authProblem(error, "code");
    await applySession(data.session);
    return { isNew: isNewAccount(data.user) };
  };
  return keep ? hold(run) : run();
}

/**
 * A new account with a password (lab43-auth docs/SETUP.md → "Passwords in an app"): Supabase emails a confirmation
 * code. `via`: the code to expect — "signup" (a new account), "email" (this address already has an account: a sign-in
 * code instead, and the password is set once the code is in — both look the same on screen, so nobody learns whether
 * an address has an account), "done" (no confirmation needed).
 */
export async function startSignUp(email: string, password: string): Promise<{ via: "signup" | "email" | "done"; isNew?: boolean }> {
  const c = await client();
  return hold(async () => {
    const { data, error } = await c.auth.signUp({ email: email.trim(), password, options: { data: newAccountData() } });
    if (error) {
      if (error.code === "user_already_exists" || /already registered/i.test(error.message)) {
        await sendEmailCode(email, { existingOnly: true });
        return { via: "email" as const };
      }
      throw authProblem(error, "signin");
    }
    if (data.session) {
      // (only when the project doesn't ask to confirm emails)
      rememberPassword(data.user?.id);
      await applySession(data.session);
      return { via: "done" as const, isNew: true };
    }
    // an address that already has a confirmed account answers with a stand-in user and no email (Supabase hides that it exists)
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      await sendEmailCode(email, { existingOnly: true });
      return { via: "email" as const };
    }
    return { via: "signup" as const };
  });
}

/**
 * The code from the sign-up email → signed in, then the password is saved again: the identity project removes a password
 * set before the email was confirmed (lab43-auth migration 20261020160200_unconfirmed_password, against someone signing
 * up with another person's address), and a password set after confirming stays. `passwordProblem`: signed in, but the
 * password wasn't saved (the dialog then asks for it again).
 */
export async function finishSignUp(email: string, code: string, password: string, via: "signup" | "email") {
  return hold(async () => {
    const c = await client();
    const { data, error } = await c.auth.verifyOtp({ email: email.trim(), token: code.replace(/\s+/g, ""), type: via });
    if (error) throw authProblem(error, "code");
    await applySession(data.session);
    let passwordProblem: AuthProblem | null = null;
    const { error: pwError } = await c.auth.updateUser({ password });
    if (!pwError || pwError.code === "same_password") rememberPassword(data.user?.id);
    else passwordProblem = authProblem(pwError, "password");
    return { isNew: isNewAccount(data.user), passwordProblem };
  });
}

/** A new account's confirmation code again ("Send a new code"). */
export async function resendSignUpCode(email: string) {
  const c = await client();
  const { error } = await c.auth.resend({ type: "signup", email: email.trim() });
  if (error) throw authProblem(error, "send");
}

/**
 * "Forgot password?": Supabase emails a code (the *Reset password* email, lab43-auth supabase/templates/recovery.html;
 * the same answer whether or not the address has an account).
 */
export async function sendPasswordReset(email: string) {
  const c = await client();
  const { error } = await c.auth.resetPasswordForEmail(email.trim());
  if (error) throw authProblem(error, "send");
}

/** The code from the reset email → signed in, the dialog stays open for the new password. */
export async function verifyRecoveryCode(email: string, code: string) {
  return hold(async () => {
    const c = await client();
    const { data, error } = await c.auth.verifyOtp({ email: email.trim(), token: code.replace(/\s+/g, ""), type: "recovery" });
    if (error) throw authProblem(error, "code");
    await applySession(data.session);
  });
}

/**
 * Sets or changes the password of the signed-in account. Supabase may want more first: `code` — an emailed code
 * (*Secure password change*, for a sign-in older than 24 hours: call sendReauthCode, then again with `nonce`),
 * `current` — the current password (*Require current password*). Other refusals are thrown (AuthProblem).
 */
export async function setPassword(password: string, extra: { nonce?: string; current?: string } = {}): Promise<{ saved: true } | { saved: false; need: "code" | "current"; error?: string }> {
  const c = await client();
  const { data, error } = await c.auth.updateUser({ password, ...(extra.nonce ? { nonce: extra.nonce.replace(/\s+/g, "") } : null), ...(extra.current ? { current_password: extra.current } : null) });
  if (!error) {
    rememberPassword(data.user?.id ?? useAccount.getState().user?.id);
    return { saved: true };
  }
  if (error.code === "reauthentication_needed") return { saved: false, need: "code" };
  if (error.code === "reauthentication_not_valid") return { saved: false, need: "code", error: CODE_FAILED };
  if (error.code === "current_password_required") return { saved: false, need: "current" };
  if (error.code === "current_password_invalid") return { saved: false, need: "current", error: "That isn't your current password." };
  throw authProblem(error, "password");
}

/** Emails the code Supabase asks for before a password change (*Secure password change*). */
export async function sendReauthCode() {
  const c = await client();
  const { error } = await c.auth.reauthenticate();
  if (error) throw authProblem(error, "send");
}

export async function signOut() {
  const c = await supabase();
  await c.auth.signOut({ scope: "local" });
  await applySession(null);
}

/** Access token for our own server (Authorization header), refreshed if needed; null when signed out. */
export async function accessToken(): Promise<string | null> {
  if (useAccount.getState().status === "signed-out" || !accountsEnabled()) return null;
  const { data } = await (await supabase()).auth.getSession();
  return data.session?.access_token ?? null;
}

// ---------------- profile ----------------

async function loadProfile(userId: string): Promise<Profile | null> {
  const c = await supabase();
  const { data, error } = await c.from("profiles").select("display_name, color").eq("id", userId).maybeSingle();
  if (error || !data) return null;
  return { displayName: data.display_name as string, color: data.color as string };
}

export async function updateProfile(p: Partial<Profile>) {
  const user = useAccount.getState().user;
  if (!user) throw new AuthProblem("Sign in first.");
  const patch: Record<string, string> = {};
  if (p.displayName !== undefined) {
    const name = p.displayName.trim().slice(0, 40);
    if (!name) throw new AuthProblem("Enter a name.", "name");
    patch.display_name = name;
  }
  if (p.color !== undefined) {
    if (!/^#[0-9a-fA-F]{6}$/.test(p.color)) throw new AuthProblem("Pick a colour.", "name");
    patch.color = p.color;
  }
  const c = await client();
  const { data, error } = await c.from("profiles").update(patch).eq("id", user.id).select("display_name, color").maybeSingle();
  if (error) throw authProblem(error);
  if (data) useAccount.getState().set({ profile: { displayName: data.display_name as string, color: data.color as string } });
}

/** Deletes the account on the server (our Worker, which holds the admin key), then signs out locally. */
export async function deleteAccount(server: string) {
  const token = await accessToken();
  if (!token) throw new AuthProblem("Sign in first.");
  if (offline()) throw new AuthProblem("You're offline. Deleting the account needs the internet.");
  const r = await fetch(new URL("/api/me", server), { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
  if (!r) throw new AuthProblem("Can't reach the MC Planner server. Check your internet connection.");
  const body = await r.json().catch(() => ({})) as { error?: string };
  if (!r.ok) throw new AuthProblem(body.error ?? `Couldn't delete the account (${r.status}).`);
  await signOut().catch(() => applySession(null));
}
